import { useGetParticipantsByEventId, ParticipantByEventId } from '@pages/event/api/useGetParticipantsByEventId';
import { VirtualizedParticipantsList } from '@features/segment-card/ui/virtualized-participants-list';
import { styles as addParticipantsModalStyles } from '@features/add-participants-modal/styles';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Checkbox, PreloaderCircular } from '@shared/ui/kit';
import { InvitedParticipants } from '@widgets/segments-tab';
import { FilterParticipants } from '@features/participants';
import { SOURCE_1CUU } from '@shared/constants/sourceData';
import { REMOTE_FILTERS } from '@entities/participants';
import { ITab, Tabs } from '@shared/ui/tabs';
import * as stylex from '@stylexjs/stylex';

const VIRTUALIZATION_THRESHOLD = 50;
const ROW_HEIGHT = 56;
const CONTAINER_HEIGHT = 370;

interface CategoryParticipantsListProps {
  eventId: number;
  setSelectedParticipants: (participants: number[]) => void;
  selectedParticipants: number[];
  participantsIds?: number[];
  setParticipantsDelete: React.Dispatch<React.SetStateAction<InvitedParticipants[]>>;
  participantsDelete: InvitedParticipants[];
}

const CategoryParticipantsList = ({
  eventId,
  setSelectedParticipants,
  selectedParticipants,
  participantsIds,
  setParticipantsDelete,
  participantsDelete,
}: CategoryParticipantsListProps) => {
  const [inputText, setInputText] = useState('');
  const [displayedParticipants, setDisplayedParticipants] = useState<ParticipantByEventId[]>([]);
  const [allParticipants, setAllParticipants] = useState<ParticipantByEventId[]>([]);
  const [debouncedInputText, setDebouncedInputText] = useState('');

  const { participants, fetching } = useGetParticipantsByEventId({ eventId, source: SOURCE_1CUU });

  const tabItems: ITab[] = [{ id: 1, title: 'Список' }];
  const [activeTab] = useState<ITab>(tabItems[0]);

  useEffect(() => {
    if (Array.isArray(participants)) {
      setAllParticipants(participants);
      setDisplayedParticipants(participants);
    }
  }, [participants]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInputText(inputText.toLowerCase());
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [inputText]);

  useEffect(() => {
    if (!debouncedInputText.trim()) {
      setDisplayedParticipants(allParticipants);
    } else {
      const filtered = allParticipants.filter((participant) => {
        const fullName = `${participant.firstName} ${participant.lastName}`.toLowerCase();
        const reversed = `${participant.lastName} ${participant.firstName}`.toLowerCase();
        const position = participant.position?.toLowerCase() ?? '';
        return (
          fullName.includes(debouncedInputText) ||
          reversed.includes(debouncedInputText) ||
          position.includes(debouncedInputText)
        );
      });
      setDisplayedParticipants(filtered);
    }
  }, [allParticipants, debouncedInputText]);

  const participantsIdsSet = useMemo(() => new Set(participantsIds || []), [participantsIds]);
  const selectedParticipantsSet = useMemo(() => new Set(selectedParticipants), [selectedParticipants]);
  const participantsDeleteMap = useMemo(
    () => new Map(participantsDelete.map((invitedParticipant) => [invitedParticipant.userId, invitedParticipant])),
    [participantsDelete]
  );

  const isParticipantInCategory = (participantId: number) => {
    return participantsIdsSet.has(participantId);
  };

  const handleParticipantToggle = (participant: ParticipantByEventId) => {
    const isInCategory = isParticipantInCategory(participant.id);

    if (!isInCategory) {
      const isSelected = selectedParticipantsSet.has(participant.id);
      setSelectedParticipants(
        isSelected
          ? selectedParticipants.filter((id) => id !== participant.id)
          : [...selectedParticipants, participant.id]
      );
      return;
    }

    const existingDelete = participantsDeleteMap.has(participant.id);
    setParticipantsDelete(
      existingDelete
        ? participantsDelete.filter((invitedParticipant) => invitedParticipant.userId !== participant.id)
        : [...participantsDelete, { userId: participant.id, ticketId: participant.ticketId }]
    );
  };

  const getParticipantCheckedStatus = (participantId: number) => {
    const isInCategory = isParticipantInCategory(participantId);

    if (isInCategory) {
      return !participantsDeleteMap.has(participantId);
    }

    return selectedParticipantsSet.has(participantId);
  };

  const statusAllChecked = displayedParticipants.every((participant) => {
    const isInCategory = isParticipantInCategory(participant.id);
    if (isInCategory) {
      return !participantsDeleteMap.has(participant.id);
    }
    return selectedParticipantsSet.has(participant.id);
  });

  const handleToggleAll = () => {
    const visibleUserIdsSet = new Set(displayedParticipants.map((participant) => participant.id));

    if (!statusAllChecked) {
      const newCheckedUsers = displayedParticipants.reduce<number[]>((acc, participant) => {
        if (!isParticipantInCategory(participant.id) && !selectedParticipantsSet.has(participant.id)) {
          acc.push(participant.id);
        }
        return acc;
      }, []);
      setSelectedParticipants([...selectedParticipants, ...newCheckedUsers]);
      setParticipantsDelete(
        participantsDelete.filter((invitedParticipant) => !visibleUserIdsSet.has(invitedParticipant.userId))
      );
      return;
    }

    setSelectedParticipants(selectedParticipants.filter((id) => !visibleUserIdsSet.has(id)));

    const participantsToRemove = displayedParticipants.reduce<Array<{ userId: number; ticketId: number }>>(
      (acc, participant) => {
        if (isParticipantInCategory(participant.id) && visibleUserIdsSet.has(participant.id)) {
          acc.push({ userId: participant.id, ticketId: participant.ticketId });
        }
        return acc;
      },
      []
    );
    setParticipantsDelete([...participantsDelete, ...participantsToRemove]);
  };

  const handleInput = (text: string) => {
    setInputText(text);
  };

  const renderParticipantRow = useCallback(
    (participant: ParticipantByEventId) => (
      <button
        type="button"
        {...stylex.props(addParticipantsModalStyles.checkboxWithName)}
        onClick={() => handleParticipantToggle(participant)}
      >
        <Checkbox
          onChange={() => handleParticipantToggle(participant)}
          size="small"
          isChecked={getParticipantCheckedStatus(participant.id)}
        />
        <div {...stylex.props(addParticipantsModalStyles.participant)}>
          <p>
            {participant.firstName} {participant.lastName}
          </p>
          <p {...stylex.props(addParticipantsModalStyles.position)}>{participant.position || ''}</p>
        </div>
      </button>
    ),
    [selectedParticipants, participantsDelete, participantsIds]
  );

  const selectedParticipantsCount = useMemo(() => {
    const participantsInCategory = (participantsIds || []).filter((id) => !participantsDeleteMap.has(id));
    return [...new Set([...participantsInCategory, ...selectedParticipants])];
  }, [participantsIds, participantsDeleteMap, selectedParticipants]);

  const renderParticipantsContent = () => {
    if (fetching) {
      return (
        <div {...stylex.props(addParticipantsModalStyles.loaderOverlay)}>
          <PreloaderCircular />
        </div>
      );
    }

    if (inputText && !displayedParticipants.length) {
      return (
        <div {...stylex.props(addParticipantsModalStyles.participants)}>
          <div {...stylex.props(addParticipantsModalStyles.emptyState)}>Поиск не дал результатов</div>
        </div>
      );
    }

    return (
      <div {...stylex.props(addParticipantsModalStyles.participants)}>
        <div {...stylex.props(addParticipantsModalStyles.blockAllList)}>
          <div {...stylex.props(addParticipantsModalStyles.checkboxWithName)}>
            <Checkbox onChange={handleToggleAll} size="small" isChecked={statusAllChecked} />
            <div {...stylex.props(addParticipantsModalStyles.participant)}>Выбрать всех сотрудников</div>
          </div>

          {displayedParticipants.length >= VIRTUALIZATION_THRESHOLD ? (
            <VirtualizedParticipantsList
              items={displayedParticipants}
              renderItem={(participant) => (
                <div style={{ minHeight: ROW_HEIGHT, display: 'flex' }}>{renderParticipantRow(participant)}</div>
              )}
              itemHeight={ROW_HEIGHT}
              height={CONTAINER_HEIGHT}
              scrollResetKey={debouncedInputText}
            />
          ) : (
            <div className="custom-scroll">
              <div {...stylex.props(addParticipantsModalStyles.allList(false))}>
                {displayedParticipants.map((participant) => (
                  <React.Fragment key={participant.id}>{renderParticipantRow(participant)}</React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div {...stylex.props(addParticipantsModalStyles.container, addParticipantsModalStyles.loaderContainer)}>
      <div {...stylex.props(addParticipantsModalStyles.modalContainer(false))}>
        <div {...stylex.props(addParticipantsModalStyles.content)}>
          <FilterParticipants
            inputText={inputText}
            onChangeSearch={handleInput}
            dateRange={[null, null]}
            onChangeDates={() => {}}
            remoteFilter={REMOTE_FILTERS.ALL}
            onChangeRemoteFilter={() => {}}
            showFilter={false}
          />

          <div {...stylex.props(addParticipantsModalStyles.participantsColumn)}>
            <div {...stylex.props(addParticipantsModalStyles.inputWithSelectedParticipant)}>
              <div {...stylex.props(addParticipantsModalStyles.tabsContainer)}>
                <Tabs tabs={tabItems} activeTab={activeTab} onChange={() => {}} size="medium" />
                <p {...stylex.props(addParticipantsModalStyles.participantCount)}>
                  Выбрано: {selectedParticipantsCount.length}
                </p>
              </div>
            </div>

            {renderParticipantsContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryParticipantsList;
